// src/pages/Reviews.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Star, Search, Filter, ChevronRight, Award } from 'lucide-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { format } from 'date-fns';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState(0);
  const { supabase } = useSupabase();

  useEffect(() => {
    fetchReviews();
  }, []);

  useEffect(() => {
    let filtered = reviews;

    if (searchTerm) {
      filtered = filtered.filter(review =>
        review.car_model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (ratingFilter > 0) {
      filtered = filtered.filter(review => review.rating >= ratingFilter);
    }

    setFilteredReviews(filtered);
  }, [searchTerm, ratingFilter, reviews]);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching reviews:', error);
      } else {
        setReviews(data || []);
        setFilteredReviews(data || []);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const RatingDisplay = ({ rating }) => {
    return (
      <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-lg">
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`h-4 w-4 ${
                i < rating
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-400'
              }`}
            />
          ))}
        </div>
        <span className="text-sm font-black text-white">{rating}/10</span>
      </div>
    );
  };

  const ReviewCard = ({ review }) => (
    <Link to={`/read/${review.id}`}>
      <div className="group h-full bg-white rounded-2xl shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden border border-gray-100 flex flex-col">
        {/* Image with Rating Badge */}
        {review.image_url && (
          <div className="relative h-56 overflow-hidden bg-gray-100">
            <img
              src={review.image_url}
              alt={review.car_model}
              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute top-4 right-4">
              <RatingDisplay rating={review.rating} />
            </div>
          </div>
        )}
        
        {/* Content */}
        <div className="p-6 flex flex-col flex-1">
          <div className="flex items-center gap-2 text-xs text-gray-500 font-semibold mb-3">
            <Calendar className="w-4 h-4" />
            {format(new Date(review.created_at), 'MMM d, yyyy')}
          </div>
          
          <h3 className="text-2xl font-black text-gray-900 mb-3 group-hover:text-red-600 transition-colors leading-tight">
            {review.car_model}
          </h3>
          
          <p className="text-gray-600 text-sm line-clamp-3 mb-6 flex-1 leading-relaxed">
            {review.excerpt}
          </p>
          
          <div className="flex items-center gap-2 text-red-600 font-black text-xs uppercase tracking-wide group-hover:gap-3 transition-all">
            Read Full Review <ChevronRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );

  // Skeleton Loader
  const ReviewsSkeleton = () => (
    <div className="animate-pulse min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="h-12 w-48 bg-gray-300 rounded mx-auto mb-4"></div>
          <div className="h-6 w-96 bg-gray-300 rounded mx-auto"></div>
        </div>
        <div className="flex flex-col md:flex-row gap-4 mb-12">
          <div className="flex-1 h-12 bg-gray-300 rounded-xl"></div>
          <div className="w-48 h-12 bg-gray-300 rounded-xl"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden">
              <div className="h-56 bg-gray-300"></div>
              <div className="p-6 space-y-4">
                <div className="h-4 w-24 bg-gray-300 rounded"></div>
                <div className="h-6 w-full bg-gray-300 rounded"></div>
                <div className="h-16 w-full bg-gray-300 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) return <ReviewsSkeleton />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white font-sans text-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-6 bg-red-600/10 text-red-600 px-4 py-2 rounded-full text-sm font-black">
            <Award className="w-4 h-4" />
            Expert Ratings
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-4 leading-tight">
            Car Reviews
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            In-depth analysis, honest driving impressions, and expert verdicts on the latest vehicles
          </p>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search Bar */}
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 group-focus-within:text-red-600 transition-colors" />
              <input
                type="text"
                placeholder="Search car models..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-6 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all text-base"
              />
            </div>
            
            {/* Rating Filter */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Filter className="h-5 w-5 text-red-600" />
                </div>
                <span className="text-sm font-black text-gray-700">Min Rating:</span>
              </div>
              <select
                value={ratingFilter}
                onChange={(e) => setRatingFilter(Number(e.target.value))}
                className="flex-1 border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent font-semibold transition-all"
              >
                <option value={0}>All Ratings</option>
                <option value={4}>⭐ 4+ Stars</option>
                <option value={3}>⭐ 3+ Stars</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        {filteredReviews.length > 0 && (
          <div className="mb-8 text-sm font-semibold text-gray-600">
            Showing <span className="text-red-600 font-black">{filteredReviews.length}</span> {filteredReviews.length === 1 ? 'review' : 'reviews'}
          </div>
        )}

        {/* Reviews Grid */}
        {filteredReviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredReviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="py-20">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-2xl mb-6">
                <Star className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-2xl font-black text-gray-900 mb-2">
                {searchTerm || ratingFilter > 0 ? 'No reviews found' : 'No reviews yet'}
              </h3>
              <p className="text-gray-600 max-w-md mx-auto mb-8">
                {searchTerm || ratingFilter > 0
                  ? 'Try adjusting your search or filter criteria'
                  : 'Check back later for the latest car reviews'}
              </p>
              {(searchTerm || ratingFilter > 0) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setRatingFilter(0);
                  }}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-black transition-all transform hover:scale-105"
                >
                  Clear Filters <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="relative py-20 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-black mt-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-0 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-700 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
            Explore More <span className="text-red-500">Reviews</span>
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
            Discover detailed reviews, ratings, and comparisons for all the latest car models.
          </p>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-black transition-all transform hover:scale-105 shadow-lg shadow-red-900/30"
          >
            Back to Home <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Reviews;